import React, { useState, useEffect } from 'react';
import { AlertTriangle, Phone, X, MapPin, Loader2, Shield, CheckCircle, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const SOSButtonGlobal = () => {
  const { isAuthenticated, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [alertSent, setAlertSent] = useState(false);
  const [activeAlert, setActiveAlert] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [countdown, setCountdown] = useState(3);
  
  // For non-logged in users
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [showGuestForm, setShowGuestForm] = useState(false);

  // Get location when dialog opens
  useEffect(() => {
    if (isOpen && !location) {
      getLocation();
    }
  }, [isOpen]);

  // Countdown timer for confirmation
  useEffect(() => {
    let timer;
    if (isConfirming && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (isConfirming && countdown === 0) {
      sendSOSAlert();
    }
    return () => clearTimeout(timer);
  }, [isConfirming, countdown]);

  const getLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        
        // Try to get address from coordinates
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await response.json();
          if (data.display_name) {
            setLocation(prev => ({ ...prev, address: data.display_name }));
          }
        } catch (e) {
          console.log('Could not get address');
        }
      },
      (error) => {
        setLocationError('Could not get location. Please enable location services.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSOSClick = () => {
    if (!isAuthenticated && !guestName && !guestPhone) {
      setShowGuestForm(true);
    } else {
      startConfirmation();
    }
  };

  const startConfirmation = () => {
    setShowGuestForm(false);
    setIsConfirming(true);
    setCountdown(3); // Reduced to 3 seconds for emergencies
  };

  const cancelConfirmation = () => {
    setIsConfirming(false);
    setCountdown(3);
  };

  const sendSOSAlert = async () => {
    setIsSending(true);
    try {
      const alertData = {
        latitude: location?.latitude || 0,
        longitude: location?.longitude || 0,
        location_address: location?.address || 'Location unavailable',
        alert_type: 'sos',
        message: 'Emergency SOS activated',
        // Guest info for non-logged in users
        guest_name: !isAuthenticated ? guestName : null,
        guest_phone: !isAuthenticated ? guestPhone : null,
      };

      // Use different endpoint for guests vs authenticated users
      const endpoint = isAuthenticated ? '/sos/alert' : '/sos/alert/guest';
      const response = await api.post(endpoint, alertData);
      
      if (response.data.success) {
        setActiveAlert(response.data.alert);
        setAlertSent(true);
        toast.success('🚨 SOS Alert Sent! Help is on the way!');
      }
    } catch (error) {
      toast.error('Failed to send alert. Please call 911 directly!');
      console.error('SOS Error:', error);
    } finally {
      setIsSending(false);
      setIsConfirming(false);
    }
  };

  const cancelAlert = async () => {
    if (!activeAlert) return;
    
    try {
      const endpoint = isAuthenticated 
        ? `/sos/alerts/${activeAlert.id}/cancel`
        : `/sos/alerts/${activeAlert.id}/cancel/guest`;
      await api.delete(endpoint);
      setAlertSent(false);
      setActiveAlert(null);
      setIsOpen(false);
      toast.info('SOS Alert cancelled');
    } catch (error) {
      toast.error('Could not cancel alert');
    }
  };

  const call911 = () => {
    window.location.href = 'tel:911';
  };

  return (
    <>
      {/* Floating SOS Button - ALWAYS VISIBLE */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-[9999] w-16 h-16 bg-red-600 hover:bg-red-700 rounded-full shadow-2xl hover:shadow-red-500/50 transform hover:scale-110 transition-all duration-200 flex items-center justify-center animate-pulse-slow"
        style={{ 
          boxShadow: '0 0 20px rgba(220, 38, 38, 0.6), 0 0 40px rgba(220, 38, 38, 0.3)',
        }}
        data-testid="global-sos-button"
        title="Emergency SOS - No Login Required"
        aria-label="Emergency SOS Button"
      >
        <div className="relative">
          <Shield className="w-8 h-8 text-white" />
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
          </span>
        </div>
      </button>

      {/* SOS Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md bg-white border-red-300 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-7 h-7" />
              Emergency SOS
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {!alertSent 
                ? 'Send an emergency alert to dispatch. No login required.'
                : 'Your SOS alert has been sent. Help is on the way.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Location Status */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
              <MapPin className={`w-5 h-5 ${location ? 'text-green-600' : 'text-gray-400 animate-pulse'}`} />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {location ? 'Location Found' : 'Getting Your Location...'}
                </p>
                {location?.address && (
                  <p className="text-xs text-gray-500 truncate">{location.address}</p>
                )}
                {locationError && (
                  <p className="text-xs text-red-500">{locationError}</p>
                )}
              </div>
              {location && <CheckCircle className="w-5 h-5 text-green-600" />}
            </div>

            {!alertSent ? (
              <>
                {/* Guest Info Form - Only for non-logged in users */}
                {showGuestForm && !isAuthenticated && (
                  <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Quick Info (Optional but helpful)
                    </p>
                    <Input
                      placeholder="Your name"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      className="bg-white border-gray-300"
                    />
                    <Input
                      placeholder="Phone number"
                      type="tel"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      className="bg-white border-gray-300"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={startConfirmation}
                        className="flex-1 bg-red-600 hover:bg-red-700"
                      >
                        Continue with SOS
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setGuestName('');
                          setGuestPhone('');
                          startConfirmation();
                        }}
                      >
                        Skip
                      </Button>
                    </div>
                  </div>
                )}

                {/* Confirmation Countdown */}
                {isConfirming && (
                  <div className="text-center py-6">
                    <div className="relative w-24 h-24 mx-auto mb-4">
                      <div className="absolute inset-0 rounded-full border-4 border-red-200"></div>
                      <div 
                        className="absolute inset-0 rounded-full border-4 border-red-600 border-t-transparent animate-spin"
                        style={{ animationDuration: '1s' }}
                      ></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-4xl font-bold text-red-600">{countdown}</span>
                      </div>
                    </div>
                    <p className="text-xl font-bold text-gray-900 mb-2">
                      Sending SOS in {countdown}...
                    </p>
                    <Button
                      variant="outline"
                      onClick={cancelConfirmation}
                      className="border-gray-300"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                )}

                {/* Main Buttons - Show when not in guest form or confirming */}
                {!showGuestForm && !isConfirming && (
                  <>
                    {/* Large SOS Button */}
                    <button
                      onClick={handleSOSClick}
                      disabled={isSending}
                      className="w-full py-8 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-2xl transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 shadow-lg hover:shadow-red-500/30"
                      data-testid="sos-send-btn"
                    >
                      {isSending ? (
                        <>
                          <Loader2 className="w-8 h-8 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="w-8 h-8" />
                          SEND SOS ALERT
                        </>
                      )}
                    </button>

                    {/* Call 911 Button */}
                    <button
                      onClick={call911}
                      className="w-full py-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xl transition-all duration-200 flex items-center justify-center gap-3"
                      data-testid="call-911-btn"
                    >
                      <Phone className="w-6 h-6" />
                      CALL 911
                    </button>

                    <p className="text-xs text-center text-gray-500">
                      Your location will be shared with dispatch.
                      <br />
                      <strong>For life-threatening emergencies, always call 911.</strong>
                    </p>
                  </>
                )}
              </>
            ) : (
              /* Alert Sent State */
              <div className="text-center py-4">
                <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center animate-bounce-slow">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-green-600 mb-2">
                  Alert Sent!
                </h3>
                <p className="text-gray-600 mb-6">
                  Dispatch has been notified.
                  <br />
                  <strong>Stay calm. Help is coming.</strong>
                </p>
                
                <div className="space-y-3">
                  <button
                    onClick={call911}
                    className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-lg flex items-center justify-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    Call 911
                  </button>
                  
                  <button
                    onClick={cancelAlert}
                    className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel Alert (False Alarm)
                  </button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.9; transform: scale(1.05); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 1s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default SOSButtonGlobal;
